import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Play, Tv, ChevronDown, Radio, Youtube, Globe, Monitor } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';
import { SEO } from '@/components/common/SEO';

// Platform icon + color mapping
const PLATFORM_CONFIG = {
  youtube: { label: 'YouTube', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
  uscreen: { label: 'NLS+', color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/20' },
  mux: { label: 'NLS+', color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/20' },
  facebook: { label: 'Facebook', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  twitch: { label: 'Twitch', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
  other: { label: 'Stream', color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/20' },
};

const formatDayHeader = (dateStr) => {
  const d = new Date(dateStr);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  const isToday = d.toDateString() === now.toDateString();
  const isTomorrow = d.toDateString() === tomorrow.toDateString();

  if (isToday) return 'Today';
  if (isTomorrow) return 'Tomorrow';

  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
};

const formatGameTime = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const getStatusInfo = (status) => {
  switch (status?.toLowerCase()) {
    case 'live': return { label: 'LIVE', class: 'text-red-500 font-bold animate-pulse' };
    case 'finished': return { label: 'FT', class: 'text-gray-500 font-medium' };
    case 'halftime': return { label: 'HT', class: 'text-yellow-500 font-medium' };
    case 'postponed': return { label: 'PPD', class: 'text-orange-500 font-medium' };
    default: return null;
  }
};

export const FixturesPage = ({ sports, leagues: allLeagues }) => {
  const [games, setGames] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedSport, setSelectedSport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [gamesRes, fixturesRes, leaguesRes] = await Promise.all([
          api.getGames({ limit: 100 }),
          api.getFixtures(selectedSport ? { sport_id: selectedSport } : {}),
          allLeagues?.length ? Promise.resolve({ data: allLeagues }) : api.getLeagues()
        ]);
        const gamesRaw = gamesRes.data;
        const gamesData = Array.isArray(gamesRaw) ? gamesRaw : (gamesRaw?.data || []);
        setGames(gamesData);
        const fixRaw = fixturesRes.data;
        setFixtures(Array.isArray(fixRaw) ? fixRaw : (fixRaw?.data || []));
        const leagueRaw = leaguesRes.data;
        setLeagues(Array.isArray(leagueRaw) ? leagueRaw : (leagueRaw?.data || allLeagues || []));
      } catch (error) {
        console.error('Error loading schedule:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedSport]);

  // Merge games + fixtures, deduplicate, group by date then league
  const schedule = useMemo(() => {
    const leagueMap = {};
    leagues.forEach(l => { leagueMap[l.id] = l; });

    // Combine games and fixtures into a unified list
    const allItems = [];

    games.forEach(g => {
      if (selectedSport) {
        const league = leagueMap[g.league_id];
        if (league) {
          const sport = sports.find(s => s.id === league.sport_id);
          if (sport && sport.id !== selectedSport) return;
        }
      }
      allItems.push({
        id: g.id,
        home_team: g.home_team_name || g.title || 'TBD',
        away_team: g.away_team_name || '',
        scheduled_time: g.scheduled_time,
        status: g.status,
        home_score: g.home_score,
        away_score: g.away_score,
        league_id: g.league_id,
        league_name: g.league_name || leagueMap[g.league_id]?.name || 'Other Games',
        league_logo: leagueMap[g.league_id]?.logo,
        sport_name: g.sport_name || leagueMap[g.league_id]?.sport_name || '',
        video_sources: g.video_sources || [],
        venue: g.venue,
        type: 'game',
      });
    });

    fixtures.forEach(f => {
      // Skip if already exists as a game (by matching teams + time)
      const isDupe = allItems.some(item =>
        item.home_team === f.home_team_name &&
        item.away_team === f.away_team_name &&
        item.scheduled_time === f.scheduled_time
      );
      if (isDupe) return;

      allItems.push({
        id: f.id,
        home_team: f.home_team_name || 'TBD',
        away_team: f.away_team_name || 'TBD',
        scheduled_time: f.scheduled_time,
        status: f.status || 'scheduled',
        league_id: f.league_id,
        league_name: f.league_name || leagueMap[f.league_id]?.name || 'Other Games',
        league_logo: leagueMap[f.league_id]?.logo,
        sport_name: f.sport_name || '',
        video_sources: f.video_sources || [],
        venue: f.venue,
        has_coverage: f.has_coverage,
        type: 'fixture',
      });
    });

    // Sort by time
    allItems.sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));

    // Group by date, then by league within each date
    const byDate = {};
    allItems.forEach(item => {
      if (!item.scheduled_time) return;
      const dateKey = new Date(item.scheduled_time).toDateString();
      if (!byDate[dateKey]) byDate[dateKey] = {};
      const leagueKey = item.league_id && leagueMap[item.league_id] ? item.league_id : '_other';
      if (!byDate[dateKey][leagueKey]) {
        byDate[dateKey][leagueKey] = {
          league_name: item.league_name,
          league_logo: item.league_logo,
          sport_name: item.sport_name,
          games: [],
        };
      }
      byDate[dateKey][leagueKey].games.push(item);
    });

    // Convert to sorted array
    return Object.entries(byDate)
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .map(([dateStr, leagueGroups]) => ({
        date: dateStr,
        leagues: Object.values(leagueGroups).sort((a, b) => a.league_name.localeCompare(b.league_name)),
      }));
  }, [games, fixtures, leagues, selectedSport, sports]);

  return (
    <div className="min-h-screen bg-gray-950" data-testid="fixtures-page">
      <SEO title="Schedule | NLS" description="Full schedule of upcoming and past games on New Logical Sports" />

      {/* Header */}
      <div className="border-b border-gray-800" style={{ backgroundColor: '#1a3a52' }}>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Calendar className="w-7 h-7 text-sky-400" />
            Schedule
          </h1>
          <p className="text-gray-400 mt-1 text-sm">All games and streams across NLS</p>

          {/* Sport Filter Pills */}
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedSport(null)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                !selectedSport
                  ? 'bg-white text-gray-900'
                  : 'bg-white/10 text-gray-300 hover:bg-white/15 hover:text-white'
              }`}
              data-testid="filter-all"
            >
              All
            </button>
            {sports.map(sport => (
              <button
                key={sport.id}
                onClick={() => setSelectedSport(sport.id)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedSport === sport.id
                    ? 'bg-white text-gray-900'
                    : 'bg-white/10 text-gray-300 hover:bg-white/15 hover:text-white'
                }`}
                data-testid={`filter-${sport.slug}`}
              >
                {sport.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Schedule Body */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-6 bg-gray-800 rounded w-48 mb-3" />
                <div className="h-10 bg-gray-800/50 rounded w-full mb-1" />
                <div className="h-10 bg-gray-800/50 rounded w-full mb-1" />
                <div className="h-10 bg-gray-800/50 rounded w-full" />
              </div>
            ))}
          </div>
        ) : schedule.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="w-14 h-14 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No games scheduled</p>
            <p className="text-gray-600 text-sm mt-1">Check back soon for upcoming matches</p>
          </div>
        ) : (
          <div className="space-y-8">
            {schedule.map(dayGroup => (
              <div key={dayGroup.date}>
                {/* Date Header */}
                <h2
                  className="text-lg font-bold text-sky-400 mb-3 pb-2 border-b border-sky-500/20"
                  data-testid={`date-header-${dayGroup.date}`}
                >
                  {formatDayHeader(dayGroup.date)}
                </h2>

                {/* League Groups */}
                <div className="space-y-4">
                  {dayGroup.leagues.map((leagueGroup, li) => (
                    <div key={li}>
                      {/* League Header */}
                      <div className="flex items-center gap-2 py-2 px-3 bg-gray-800/50 rounded-t-lg border-b border-gray-700/50">
                        {leagueGroup.league_logo ? (
                          <img
                            src={resolveMediaUrl(leagueGroup.league_logo)}
                            alt=""
                            className="w-5 h-5 rounded object-cover"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded bg-sky-800/60 flex items-center justify-center text-[9px] font-bold text-sky-300">
                            {leagueGroup.league_name?.[0]}
                          </div>
                        )}
                        <span className="text-white text-sm font-semibold">{leagueGroup.league_name}</span>
                        {leagueGroup.sport_name && (
                          <span className="text-gray-500 text-xs ml-1">· {leagueGroup.sport_name}</span>
                        )}
                      </div>

                      {/* Game Rows */}
                      <div className="border border-gray-800 border-t-0 rounded-b-lg overflow-hidden divide-y divide-gray-800/50">
                        {leagueGroup.games.map(game => {
                          const statusInfo = getStatusInfo(game.status);
                          const hasStream = game.video_sources?.length > 0;
                          const isFinished = game.status === 'finished';
                          const isLive = game.status === 'live';

                          return (
                            <div
                              key={game.id}
                              className={`flex items-center gap-0 hover:bg-white/[0.02] transition-colors ${
                                isLive ? 'bg-red-500/[0.03]' : ''
                              }`}
                              data-testid={`schedule-game-${game.id}`}
                            >
                              {/* Time Column */}
                              <div className="w-20 sm:w-24 shrink-0 px-3 py-2.5 text-right">
                                {statusInfo ? (
                                  <span className={`text-xs ${statusInfo.class}`}>{statusInfo.label}</span>
                                ) : (
                                  <span className="text-gray-400 text-xs font-mono">
                                    {formatGameTime(game.scheduled_time)}
                                  </span>
                                )}
                              </div>

                              {/* Score (for finished/live) */}
                              {(isFinished || isLive) && game.home_score != null ? (
                                <div className="w-12 shrink-0 text-center">
                                  <span className="text-white text-xs font-bold">
                                    {game.home_score} - {game.away_score}
                                  </span>
                                </div>
                              ) : (
                                <div className="w-12 shrink-0" />
                              )}

                              {/* Teams — clickable */}
                              <div className="flex-1 min-w-0 py-2.5 pr-2">
                                {hasStream ? (
                                  <Link
                                    to={`/watch/${game.id}`}
                                    className="text-sky-400 hover:text-sky-300 text-sm font-medium hover:underline truncate block"
                                    data-testid={`watch-link-${game.id}`}
                                  >
                                    {game.away_team ? `${game.home_team} vs ${game.away_team}` : game.home_team}
                                  </Link>
                                ) : (
                                  <span className="text-gray-300 text-sm truncate block">
                                    {game.away_team ? `${game.home_team} vs ${game.away_team}` : game.home_team}
                                  </span>
                                )}
                              </div>

                              {/* Platforms */}
                              <div className="shrink-0 flex items-center gap-1.5 px-3 py-2.5 max-w-[250px] overflow-hidden">
                                {hasStream ? (
                                  game.video_sources.map((src, si) => {
                                    const cfg = PLATFORM_CONFIG[src.type] || PLATFORM_CONFIG.other;
                                    return (
                                      <span
                                        key={si}
                                        className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color} whitespace-nowrap`}
                                      >
                                        {src.is_free && '▸ '}{cfg.label}
                                      </span>
                                    );
                                  })
                                ) : game.has_coverage ? (
                                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-emerald-500/10 border-emerald-500/20 text-emerald-400">
                                    Coverage
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FixturesPage;
